"use strict";
/*
  AZP-7074 — Model Finder ("Common Model Number Locations") modal.

  Markup: snippets/model-locator-modal.liquid. Opening/closing, focus trap and
  the ESC/backdrop handling are the theme's generic <modal-dialog> (scripts.js);
  this file only adds:
    - a delegated opener for every [data-model-locator-open] on the page,
    - the appliance / diagram switching inside the modal,
    - the two-screen behaviour on mobile (list -> detail with a back button),
    - the desktop arrows for the horizontally scrolling diagram tab row.
*/

(function () {
  const OPEN_SELECTOR = "[data-model-locator-open]";
  const MODAL_ID = "ModelLocatorModal";
  const MOBILE_QUERY = "(max-width: 989px)";

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  document.addEventListener("click", (event) => {
    const opener = event.target.closest(OPEN_SELECTOR);
    if (!opener) return;
    const modal = getModal();
    if (!modal || typeof modal.show !== "function") return;
    event.preventDefault();
    modal.show(opener);
    const locator = modal.querySelector("model-locator");
    if (locator) locator.onOpen();
  });

  // Deep link: /?model-locator=open or #model-locator
  window.addEventListener("DOMContentLoaded", () => {
    const wantsOpen =
      window.location.hash === "#model-locator" ||
      new URLSearchParams(window.location.search).get("model-locator") === "open";
    if (!wantsOpen) return;
    const modal = getModal();
    if (modal && typeof modal.show === "function") {
      modal.show(document.body);
      const locator = modal.querySelector("model-locator");
      if (locator) locator.onOpen();
    }
  });

  if (customElements.get("model-locator")) return;

  class ModelLocator extends HTMLElement {
    constructor() {
      super();
      this.mobile = window.matchMedia(MOBILE_QUERY);
      this.defaultTitle = this.dataset.title || "";
      this.titleEl = this.querySelector("[data-ml-title]");
      this.backButton = this.querySelector("[data-ml-back]");
      this.emptyState = this.querySelector("[data-ml-empty]");
      this.items = Array.from(this.querySelectorAll("[data-ml-appliance]"));
      this.panels = Array.from(this.querySelectorAll("[data-ml-appliance-panel]"));
      this.current = null;

      this.tabSliders = this.buildTabSliders();

      this.addEventListener("click", this.onClick.bind(this));
      if (this.backButton) {
        this.backButton.addEventListener("click", () => this.showList());
      }
      this.mobile.addEventListener("change", () => this.syncLayout());
      window.addEventListener("resize", () => this.syncTabNav());
    }

    onOpen() {
      // Desktop opens on the overview grid; mobile opens on the list.
      this.syncLayout();
      this.syncTabNav();
    }

    buildTabSliders() {
      return Array.from(this.querySelectorAll("[data-ml-tabs-slider]")).map((slider) => {
        const entry = {
          track: slider.querySelector("[data-ml-tabs]"),
          prev: slider.querySelector("[data-ml-tabs-prev]"),
          next: slider.querySelector("[data-ml-tabs-next]"),
        };

        const step = () => {
          const tab = entry.track.firstElementChild;
          const gap = parseFloat(window.getComputedStyle(entry.track).columnGap) || 0;
          return tab ? tab.getBoundingClientRect().width + gap : entry.track.clientWidth;
        };

        entry.prev.addEventListener("click", () => {
          entry.track.scrollBy({ left: -step(), behavior: "smooth" });
        });
        entry.next.addEventListener("click", () => {
          entry.track.scrollBy({ left: step(), behavior: "smooth" });
        });
        entry.track.addEventListener("scroll", () => this.syncTabNav(entry), { passive: true });

        return entry;
      });
    }

    syncTabNav(only) {
      const entries = only ? [only] : this.tabSliders;
      entries.forEach(({ track, prev, next }) => {
        // A panel that is still hidden measures 0 — leave its arrows until it is shown.
        if (!track.clientWidth) return;
        const max = track.scrollWidth - track.clientWidth;
        prev.hidden = track.scrollLeft <= 1;
        next.hidden = track.scrollLeft >= max - 1;
      });
    }

    onClick(event) {
      const item = event.target.closest("[data-ml-appliance]");
      if (item) {
        event.preventDefault();
        this.select(item.dataset.mlAppliance);
        return;
      }

      const tab = event.target.closest("[data-ml-diagram]");
      if (tab) {
        event.preventDefault();
        this.selectDiagram(tab);
      }
    }

    select(handle) {
      this.current = handle;
      this.classList.add("has-selection");

      this.items.forEach((item) => {
        const active = item.dataset.mlAppliance === handle;
        item.setAttribute("aria-pressed", active ? "true" : "false");
        item.classList.toggle("is-active", active);
      });

      let activePanel = null;
      this.panels.forEach((panel) => {
        const active = panel.dataset.mlAppliancePanel === handle;
        panel.hidden = !active;
        if (active) activePanel = panel;
      });

      if (this.emptyState) this.emptyState.hidden = true;

      if (this.mobile.matches) {
        this.classList.add("is-detail");
        if (this.backButton) this.backButton.hidden = false;
        if (this.titleEl && activePanel) {
          this.titleEl.textContent = activePanel.dataset.mlName || this.defaultTitle;
        }
        this.classList.add("is-detail-title");
      }

      const panelScroller = this.querySelector(".ml-panel");
      if (panelScroller) panelScroller.scrollTop = 0;

      this.syncTabNav();
    }

    selectDiagram(tab) {
      const appliance = tab.closest("[data-ml-appliance-panel]");
      if (!appliance) return;
      const handle = tab.dataset.mlDiagram;

      appliance.querySelectorAll("[data-ml-diagram]").forEach((el) => {
        el.setAttribute("aria-selected", el === tab ? "true" : "false");
      });
      appliance.querySelectorAll("[data-ml-diagram-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.mlDiagramPanel !== handle;
      });
    }

    showList() {
      this.classList.remove("is-detail", "is-detail-title");
      if (this.backButton) this.backButton.hidden = true;
      if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
      const navScroller = this.querySelector(".ml-nav");
      if (navScroller) navScroller.scrollTop = 0;
    }

    syncLayout() {
      if (!this.mobile.matches) {
        // Desktop never uses the stacked screens.
        this.classList.remove("is-detail", "is-detail-title");
        if (this.backButton) this.backButton.hidden = true;
        if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
      } else if (this.current) {
        this.classList.add("is-detail");
        if (this.backButton) this.backButton.hidden = false;
      }
    }
  }

  customElements.define("model-locator", ModelLocator);
})();
